import React from 'react';
import Divider from '@components/Divider'
import MemphisPattern from '@components/MemphisPattern'
import Navigation from '@components/Navigation'
import PageTitle from '@components/PageTitle'
import attachSocialIcons from '@helpers/attachSocialIcons'
import { Layout, Main, Stack } from '@layout'
import Seo from '@widgets/Seo'
import { Badge, Box, Card, Flex, Heading, Text } from 'theme-ui'

import getImageVariant from '@components/utils/getImageVariant'
import { GatsbyImage as Img } from 'gatsby-plugin-image'
import PropTypes from 'prop-types'
import { css } from 'theme-ui'

//Base size to keep all layers aligned easier
const bs = x => `${x * 0.35}rem`

const avatorStyles = {
  wrapper: {
    position: `relative`,
    zIndex: 2,
    textAlign: `center`,
    mb: bs(2)
  },
  pattern: {
    backgroundSize: `8rem`,
    opacity: 0.15
  },
  imageWrapper: {
    mx: `auto`,
    img: {
      borderRadius: `0 0 9999px 9999px`
    }
  }
}

const Avatar = ({
  avatar,
  withPattern,
  patternStyles = {},
  size,
  width,
  loading,
  alt
}) => {
  const image = avatar
  width = width || image.width

  return (
    <Box sx={avatorStyles.wrapper}>
      <Box>
        {withPattern && (
          <MemphisPattern sx={{ ...avatorStyles.pattern, ...patternStyles }} />
        )}
        <img src={image} width="100%" css={css(styles.imageWrapper)} />
      </Box>
    </Box>
  )
}

Avatar.defaultProps = {
  size: 'regular',
  withPattern: false
}

Avatar.propTypes = {
  size: PropTypes.oneOf([false, 'small', 'regular']),
  width: PropTypes.number,
  withPattern: PropTypes.bool,
  patternStyles: PropTypes.object,
  loading: PropTypes.string,
  alt: PropTypes.string
}


const styles = {
  card: {
    position: `relative`
  },
  wrapper: {
    flexDirection: [`column`, `row`],
    position: `relative`,
    zIndex: 3
  },
  avatarColumn: {
    flexBasis: `1/5`
  },
  infoColumn: {
    flexBasis: `4/5`
  },
  innerBox: {
    flexBasis: `1/2`,
    flexGrow: 1,
    px: [0, 3],
    mt: [3, 0]
  },
  subheader: {
    color: `omegaDark`
  },
  name: {
    textAlign: [`center`, `left`],
    mt: [3, 0],
    px: 3
  },
  bio: {
    textAlign: [`center`, `left`]
  },
  socialList: {
    a: {
      m: 0
    }
  },
  link: {
    position: `absolute`,
    top: 10,
    right: 10,
    zIndex: 3
  },
  pattern: {
    borderRadius: `lg`
  },
  gradient: {
    size: `full`,
    borderRadius: `lg`,
    position: `absolute`,
    left: 0,
    top: 0,
    zIndex: 2,
    background: [
      t =>
        `linear-gradient(0deg, ${t.colors.contentBg} 20%, rgba(255, 255, 255, 0) 80%)`,
      t =>
        `linear-gradient(270deg, ${t.colors.contentBg} 20%, rgba(255, 255, 255, 0) 100%)`
    ]
  }
}

const Subheader = ({ children }) => (
  <Heading variant='h4' sx={styles.subheader}>
    {children}
  </Heading>
)

const AuthorAvatar = ({ name, thumbnail, slug }) => {
  return thumbnail ? (
    <Box>
      <Avatar avatar={thumbnail} alt={name} />
      {/* <Link as={GLink} to={slug} aria-label={name}> </Link> */}
    </Box>
  ) : null
}


const AuthorName = ({ name, slug }) => (
  <Box sx={styles.name}>
    <Heading variant='h3'>
      {/* <Link as={GLink} to={slug}> */}
        {name}
      {/* </Link> */}
    </Heading>
  </Box>
)

const AuthorBio = ({ title, description }) => {
  return (
  <Box sx={styles.bio}>
    <Subheader>{title}</Subheader>
    <Text>{description}</Text>
  </Box>
)}

const AuthorSkills = ({ skills }) =>
  skills ? (
    <Box sx={styles.innerBox}>
      <Subheader>Expertise</Subheader>
      {skills.map(skill => (
        <Text key={`skill-${skill}`}>{skill}</Text>
      ))}
    </Box>
  ) : null

const AuthorSocialMedia = ({ social }) =>
  social ? (
    <Box sx={styles.innerBox}>
      <Subheader>Social Media</Subheader>
      <Navigation
        variant='vertical'
        items={attachSocialIcons(social)}
        wrapperStyle={styles.socialList}
      />
    </Box>
  ) : null

const AuthorExpanded = ({ author, withLink }) => {
  if (!author) return null

  const { skills, social, profilePage } = author

  return (
    <Card variant='paper' sx={styles.card}>
      <Flex sx={styles.wrapper}>
        <Box sx={styles.avatarColumn}>
          <AuthorAvatar {...author} />
        </Box>
        <Box sx={styles.infoColumn}>
          <AuthorName {...author} />
          <Flex sx={styles.wrapper}>
            <Box sx={styles.innerBox}>
              <AuthorBio {...author} />
              {profilePage && (<a href={profilePage.url} target="_blank">{profilePage.title}</a>)}
            </Box>
            {(social || skills) && (
              <Box sx={styles.innerBox}>
                <Flex>
                  <AuthorSkills {...author} />
                  <AuthorSocialMedia {...author} />
                </Flex>
              </Box>
            )}
          </Flex>
        </Box>
      </Flex>
      {withLink && (
        <Badge variant='tag' as={GLink} to={author.slug} sx={styles.link}>
          View Posts
        </Badge>
      )}
      <Box sx={styles.gradient} />
      <MemphisPattern sx={styles.pattern} />
    </Card>
  )
}

const PageContact = props => {
  const authors = [
    {
      "name": "太刀川 弘和 ( Hirokazu TACHIKAWA）",
      "title": "筑波大学教授、精神科医",
      "description": "太刀川 弘和",
      "thumbnail": "https://d2x2vsuirtdswj.cloudfront.net/tachikawa.jpg",
      "profilePage": {
        "title": "TRIOS 筑波大学研究者総覧 (tsukuba.ac.jp)",
        "url": "https://trios.tsukuba.ac.jp/researcher/0000002283",
      },
    },
    {
      "name": "高橋　晶 ( Sho TAKAHASHI)",
      "title": "筑波大学准教授、精神科医",
      "description": "",
      "thumbnail": "https://d2x2vsuirtdswj.cloudfront.net/takahashi.jpg",
      "profilePage": {
        "title": "TRIOS 筑波大学研究者総覧 (tsukuba.ac.jp)",
        "url": "https://trios.tsukuba.ac.jp/researcher/0000003264",
      },
    },
    {
      "name": "笹原　信一朗 (Shinichiro SASAHARA)",
      "title": "筑波大学准教授、産業医",
      "description": "",
      "thumbnail": "https://d2x2vsuirtdswj.cloudfront.net/sasahara.jpg",
      "profilePage": {
        "title": "TRIOS 筑波大学研究者総覧 (tsukuba.ac.jp)",
        "url": "https://trios.tsukuba.ac.jp/researcher/0000001646",
      },
    },
    {
      "name": "川島　義高 ( Yoshitaka KAWASHIMA)",
      "title": "明治大学講師准教授、臨床心理士",
      "description": "",
      "thumbnail": "https://d2x2vsuirtdswj.cloudfront.net/kawashima.jpg",
      "profilePage": {
        "title": "研究者データ(https://researchmap.jp/yoshitaka.kawashima)",
        "url": "https://researchmap.jp/yoshitaka.kawashima/",
      },
    },
    {
      "name": "氏原　将奈 ( Masana UJIHARA)",
      "title": "淑徳大学助教、筑波大学大学院人間総合科学学術院博士課程、保健師",
      "description": "",
      "thumbnail": "https://d2x2vsuirtdswj.cloudfront.net/ujihara.jpg",
      "profilePage": {
        "title": "研究者データ(https://researchmap.jp/msn3a)",
        "url": "https://researchmap.jp/msn3a",
      },
    },
    {
      "name": "綿谷　恵子 ( Keiko WATAYA)",
      "title": "筑波大学附属病院、筑波大学大学院人間総合科学学術院博士課程、精神看護専門看護師",
      "description": "",
      "thumbnail": "https://d2x2vsuirtdswj.cloudfront.net/wataya.jpg",
    },
    {
      "name": "松浦　麻子 ( Asako MATSURA)",
      "title": "東邦大学助教、筑波大学大学院人間総合科学学術院博士課程、看護師",
      "description": "",
      "thumbnail": "https://d2x2vsuirtdswj.cloudfront.net/matsuura.jpg",
      "profilePage": {
        "title": "研究者データ(https://researchmap.jp/fncrj050)",
        "url": "https://researchmap.jp/fncrj050",
      },
    },
  ]
  return (<Layout {...props}>
    <Seo title='メンバー' />
    <Divider />
    <Stack>
      <Main>
        <PageTitle
          header="メンバー"
          // subheader='下記のメンバーによって本研究は実施されています。'
        />
        <Divider />
        {authors.map(author => (<AuthorExpanded key={author.name} author={author}/>))}
        
      </Main>
      {/* <Sidebar>
        <Commitment />
        <Divider />
        <ContactInfo />
      </Sidebar> */}
    </Stack>
  </Layout>)
}

export default PageContact
